/*
 * Copyright (c) 2025 CalAmp Corp.  All Rights Reserved
 */
import React, {memo, ReactNode, useEffect, useState} from 'react';
import {useSelector} from 'react-redux';
// eslint-disable-next-line no-restricted-imports
import {useRouter} from 'next/router';
import useElementResize, {UseElementResizeResult} from '@ion/core/src/hooks/useElementResize';
import usePreviousState from '@ion/dls/src/hooks/usePreviousState';
import {globalGroupIdSelector} from '../../store/selectors/global-group';

interface PersistentMapHostProps {
  routeToMatch?: string;
  children: ReactNode;
  containerId: string;
  condition?: boolean;
  show?: boolean;
  onShow?: (visible: boolean) => void;
  // resizes the map only once its container has stopped changing size - not
  // at every step of an animated layout change ...
  settleResize?: boolean;
  // places the map at these dimensions instead of its container's, while
  // given - e.g. a size known ahead of an animation that will reach it ...
  dimensionsOverride?: UseElementResizeResult | null;
  // reports each container size the map would otherwise follow ...
  onDimensionsChange?: (dimensions: UseElementResizeResult) => void;
  // hides the map on its active route - it stays mounted, with its widgets
  // and layers, unlike leaving the route ...
  hidden?: boolean;
};

/**
 * A reusable React component that mounts and persists a given map component (like
 * TrackingMapContainer or HistoryMapContainer) based on the matched route. This
 * allows the iframe or map component to persist in memory even when navigating
 * between pages, improving performance and preserving internal state.
 *
 * ```typescript
 * // Usage for Tracking map
 * import PersistentMapHost from './PersistentMapHost';
 * import TrackingMapContainer from './containers/TrackingMapContainer';
 *
 * const PersistentTrackingMapHost = () => (
 *   <PersistentMapHost routeToMatch="/tracking2">
 *     <TrackingMapContainer />
 *   </PersistentMapHost>
 * );
 *
 * // Usage for History map
 * import PersistentMapHost from './PersistentMapHost';
 * import HistoryMapContainer from './containers/HistoryMapContainer';
 *
 * const PersistentHistoryMapHost = () => (
 *   <PersistentMapHost routeToMatch="/reports">
 *     <HistoryMapContainer />
 *   </PersistentMapHost>
 * );
 * 
 * ```
 *
 * -----------------------------------------------------------------------------
 * Features:
 * - Detects if the component has ever matched the route (persisted rendering)
 * - Mounts children in a `div` only once after route match
 * - Automatically hides/shows the component using `visibility`
 * - Tracks dimensions of the map container using `useElementResize`
 * - Logs position after render (top/left)
 */
export const PersistentMapHost = ({
  containerId,
  routeToMatch,
  children,
  onShow,
  show = false,
  condition = true,
  settleResize = false,
  dimensionsOverride = null,
  onDimensionsChange,
  hidden = false
}: PersistentMapHostProps) => {
  const [visited, setVisited] = useState(false);
  const [id, setId]           = useState(null);
  const route                 = useRouter();
  const isActiveRoute         = show || (condition && (route?.route === routeToMatch));
  const globalGroupId         = useSelector(globalGroupIdSelector);
  const previous              = usePreviousState({globalGroupId});

  // hook to measure the container dimensions
  const measuredDimensions = useElementResize(id, false, undefined, {settled: settleResize});
  const dimensions         = dimensionsOverride ?? measuredDimensions;

  useEffect(() => {
    onDimensionsChange?.(measuredDimensions);
  }, [measuredDimensions]);

  useEffect(() => {
    onShow?.(isActiveRoute);
    if (isActiveRoute) {
      setVisited(true);
    }
    const timeout = setTimeout(() => {
      setId(isActiveRoute ? containerId : null);
      clearTimeout(timeout);
    }, 1000);
  }, [isActiveRoute, containerId]);

  useEffect(() => {
    if (!isActiveRoute && (globalGroupId !== previous?.globalGroupId)) {
      setVisited(false);
      onShow?.(false);
    }
  }, [globalGroupId, previous?.globalGroupId]);

  /**
   * Determines dynamic styles for the persistent container.
   */
  const getStyle: any = () => {
    if (isActiveRoute) {
      return {
        visibility : hidden ? 'hidden' : 'visible',
        position   : 'absolute',
        zIndex     : 999,
        ...dimensions
      };
    }
    return {visibility: 'hidden'};
  };

  if (visited) {
    return (
      <div style={getStyle()}>
        {children}
      </div>
    );
  };
};

export default memo(PersistentMapHost);
